import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Stack } from "@mui/material";
import Add from "@mui/icons-material/Add";
import { CrmPage, CrmPageHeader, CrmActionButton, CrmTableCard, CrmNotice, CrmRowActions, ListTruncate, ListLink, ListStatus } from "../shared/CrmUi";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { appConfirm } from "components/AppDialog";

const CompetitorTable = () => {
    const [data, setData] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [notification, setNotification] = useState({ open: false, text: "" });
    const navigate = useNavigate();

    // 🔁 Müşteri ve rakip verisini getir
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [competitorsResp, customersResp] = await Promise.all([
                    CrudService.getCompetitors(),
                    CrudService.getCustomers(),
                ]);

                const competitors = competitorsResp.data.map((item) => ({
                    id: item.id,
                    ...item.attributes,
                }));

                const customers = customersResp.data.map(({ id, attributes }) => ({
                    id,
                    name: attributes.name,
                }));

                setData(competitors);
                setCustomers(customers);
            } catch (error) {
                console.error("Error loading data:", error);
                setNotification({
                    open: true,
                    text: "Failed to load data. Please try again.",
                });
                setTimeout(() => setNotification({ open: false, text: "" }), 5000);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const rows = data.map((comp) => {
            const customerNames = Array.isArray(comp.competitorCustomer)
                ? comp.competitorCustomer.map((id) => {
                    const found = customers.find((c) => c.id === id);
                    return found ? found.name : id;
                })
                : [];

            return {
                id: comp.id,

                competitorCompany: (
                    <ListLink onClick={() => handleEdit(comp.id)}><ListTruncate text={comp.competitorCompany || "—"} max={40} /></ListLink>
                ),

                competitorRepresentative: comp.competitorRepresentative || "-",

                competitorCustomer: customerNames.length > 0 ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {customerNames.map((name, i) => (
                            <ListStatus key={`customer-${i}`} label={name} tone="navy" />
                        ))}
                    </Stack>
                ) : (
                    "-"
                ),

                competitorProduct: Array.isArray(comp.competitorProduct) &&
                comp.competitorProduct.length > 0 ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {comp.competitorProduct.map((product, i) => (
                            <ListStatus key={`product-${i}`} label={product} tone="navy" />
                        ))}
                    </Stack>
                ) : (
                    "-"
                ),

                competitorComment: (
                    <ListTruncate text={comp.competitorComment || "—"} max={40} />
                ),

                actions: (
                    <CrmRowActions onEdit={() => handleEdit(comp.id)} editTitle="Edit Competitor" onDelete={(e) => handleDelete(e, comp.id)} deleteTitle="Delete Competitor" />
                ),
            };
        });

        setTableData(rows);
    }, [data, customers]);

    const handleEdit = (id) => {
        navigate(`/competitor/create-competitor/details?id=${id}`);
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!await appConfirm("Are you sure you want to delete this competitor?"))
            return;

        try {
            await CrudService.deleteCompetitor(id);
            setData((prev) => prev.filter((item) => item.id !== id));
            setNotification({
                open: true,
                text: "Competitor deleted successfully.",
            });
        } catch {
            setNotification({
                open: true,
                text: "Delete failed. Please try again.",
            });
        } finally {
            setTimeout(() => setNotification({ open: false, text: "" }), 5000);
        }
    };

    const handleAdd = () => {
        navigate("/competitor/create-competitor/details");
    };

    const columns = [
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Company</span>
            ),
            accessor: "competitorCompany",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>
          Representative
        </span>
            ),
            accessor: "competitorRepresentative",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>
          Customer(s)
        </span>
            ),
            accessor: "competitorCustomer",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>
          Product(s)
        </span>
            ),
            accessor: "competitorProduct",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Comment</span>
            ),
            accessor: "competitorComment",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>
            ),
            accessor: "actions",
            disableSortBy: true,
        },
    ];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <CrmPage>
                <CrmPageHeader
                    title="Competitors"
                    subtitle="Competitor companies, their representatives, shared customers and products."
                    actions={
                        <CrmActionButton icon={<Add />} onClick={handleAdd}>
                            Add Competitor
                        </CrmActionButton>
                    }
                />
                {notification.open && <CrmNotice tone="blue">{notification.text}</CrmNotice>}
                <CrmTableCard title="Competitor List" summary={`${tableData.length} records`}>
                    <DataTable table={{ columns, rows: tableData }} canSearch />
                </CrmTableCard>
            </CrmPage>
            <Footer />
        </DashboardLayout>
    );
};

export default CompetitorTable;
